import axios from './Axios';
import SERVER_URL from './serverUrl';

/**
 * Open the Webflow app-install flow in a new tab.
 *
 * Two things make this fiddlier than a plain window.open:
 *
 * 1. Popup blocking. The tab must be opened synchronously inside the click gesture. Awaiting
 *    the request first and opening afterwards puts window.open outside the gesture, and the
 *    browser blocks it. So we open a blank tab immediately and navigate it once the URL is in.
 *
 * 2. Attribution. The plain /authorize endpoint carries no FlowBookings identity, so the
 *    resulting OAuth grant can't be tied back to a user. The signed URL embeds a short-lived
 *    signed `state` that lets the backend attribute the install.
 *
 * Degrades safely: if the signed URL can't be minted, the un-instrumented /authorize is used
 * instead. The user still connects Webflow; only the attribution event is lost. Connecting
 * Webflow must never depend on the analytics path working.
 *
 * Lives here rather than inline because both the onboarding wizard and the standalone
 * Connect Webflow page need it, and two copies of OAuth-adjacent logic will drift.
 */
export default async function openWebflowInstall() {
  const popup = window.open('', '_blank');
  const fallbackUrl = `${SERVER_URL}/authorize`;

  let target = fallbackUrl;
  try {
    const { data } = await axios.get('/webflow/install-url');
    target = data?.url || fallbackUrl;
  } catch (err) {
    // Not fatal — but log it, or a permanently broken attribution endpoint looks like
    // "installs work fine" while every webflow_app_installed event silently goes missing.
    console.warn('Could not mint a signed Webflow install URL, falling back to /authorize:', err);
  }

  if (popup) {
    popup.location.href = target;
    // Drop the back-reference: without this the Webflow tab keeps a handle on this window
    // and could navigate it elsewhere (reverse tabnabbing).
    popup.opener = null;
  } else {
    // Popup was blocked. This second attempt is outside the click gesture and will usually
    // be blocked too, but it costs nothing and recovers the cases where it isn't.
    window.open(target, '_blank', 'noopener');
  }
}
